import React, { useState, useMemo, useRef, useEffect } from "react"
import "./styles.scss"

import DataLoader from "../DataLoader"
import AutoSuggest from "react-autosuggest"
import classnames from "classnames"
import useOnClickOutside from "use-onclickoutside"

export const unselectedCountryLabel = "Global"
// Use your imagination to render suggestions.
const renderSuggestion = (suggestion) => <div>{suggestion.Country}</div>

// Function to normalize strings by removing diacritics and converting to lowercase
//This allows for searching 'cote...' to match 'Côte d'Ivoire' among other countries
const normalizeText = (text) =>
  text
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()

const startsWith = (word, prefix) => {
  const wordNormalized = normalizeText(word)
  const prefixNormalized = normalizeText(prefix)
  const prefixLength = prefixNormalized.length
  return wordNormalized.substr(0, prefixLength) === prefixNormalized
}

const countrySortWithValue = (value) => {
  return (a, b) => {
    let aCountry = a.Country
    let bCountry = b.Country
    if (startsWith(aCountry, "The ")) {
      aCountry = aCountry.slice(4)
    }
    if (startsWith(bCountry, "The ")) {
      bCountry = bCountry.slice(4)
    }
    if (aCountry === unselectedCountryLabel) {
      return -1
    }
    if (bCountry === unselectedCountryLabel) {
      return 1
    }
    if (value !== "" && value !== null) {
      const aStartsWith = startsWith(aCountry, value)
      const bStartsWith = startsWith(bCountry, value)
      if (aStartsWith && !bStartsWith) {
        return -1
      }
      if (!aStartsWith && bStartsWith) {
        return 1
      }
    }

    if (aCountry > bCountry) {
      return 1
    }
    return -1
  }
}

export default function CountrySelect(props) {
  const { value, selectedYear } = props

  const [selectOpen, setSelectOpen] = useState(false)
  const [currentTypedInputValue, setCurrentTypedInputValue] = useState("")
  const [currentSuggestions, setCurrentSuggestions] = useState(null)
  const containerRef = useRef()
  const inputRef = useRef()

  useEffect(() => {
    if (selectOpen) {
      inputRef.current && inputRef.current.focus()
      setCurrentTypedInputValue("")
      setCurrentSuggestions(null)
    }
  }, [selectOpen])

  const close = () => {
    setSelectOpen(false)
    clear()
  }
  useOnClickOutside(containerRef, close)

  const countries = useMemo(
    () => [
      { Country: unselectedCountryLabel },
      ...[...DataLoader.dataByYear[selectedYear].countries].sort(
        countrySortWithValue(""),
      ),
    ],
    [selectedYear],
  )

  const onChangeTypeEntry = (event, { newValue }) => {
    setCurrentTypedInputValue(newValue)
  }

  const fetchSuggestions = ({ value }) => {
    const input = normalizeText(value)
    const suggestions = countries
      .filter((country) => normalizeText(country.Country).includes(input))
      .sort(countrySortWithValue(value))
    setCurrentSuggestions(suggestions)
  }

  const pickSelection = (event, { suggestion, suggestionValue }) => {
    close()
    props.onChange(suggestion)
  }

  const highlightSelection = ({ suggestion }) => props.onHighlight(suggestion)

  const toggleSelectOpen = (event) => {
    if (selectOpen) {
      close()
    } else {
      setSelectOpen(true)
    }
  }

  const clear = () => {
    inputRef.current && inputRef.current.focus()
    setCurrentTypedInputValue("")
    setCurrentSuggestions(null)
    inputRef.current && inputRef.current.focus()
  }

  const onClearButton = () => {
    props.onClear()
    clear()
  }

  const renderInput = (props) => {
    const clearButton =
      props.value === "" ? null : (
        <span className="closeButton" onClick={onClearButton}>
          &times;
        </span>
      )
    return (
      <div className="customInput">
        <input {...props} ref={inputRef} />
        {clearButton}
      </div>
    )
  }
  const autoselect = selectOpen ? (
    <AutoSuggest
      suggestions={currentSuggestions || countries}
      onSuggestionsFetchRequested={fetchSuggestions}
      getSuggestionValue={(country) => country.Country}
      renderSuggestion={renderSuggestion}
      inputProps={{
        value: currentTypedInputValue,
        onChange: onChangeTypeEntry,
      }}
      alwaysRenderSuggestions={true}
      onSuggestionSelected={pickSelection}
      onSuggestionHighlighted={highlightSelection}
      renderInputComponent={renderInput}
      focusInputOnSuggestionClick={false}
    />
  ) : null
  return (
    <div
      className={classnames("countrySelect", { open: selectOpen })}
      style={props.style}
      ref={containerRef}
    >
      <div className="countrySelectLabel" onClick={toggleSelectOpen}>
        {value === "" ? "COUNTRY" : value}
      </div>
      {autoselect}
    </div>
  )
}

CountrySelect.defaultProps = {
  onChange: () => {},
  onHighlight: () => {},
  onClear: () => {},
}
